// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';

import { InputMask, formatValueWithMask } from '@tectonics/components/input-mask';
import { useUniqueId } from '@tectonics/hooks/use-unique-id';
import { type CardBrand } from '@tectonics/prs/card-brand';
import { useUserInputAsTextFieldWithFormatting } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError } from '@tectonics/prs/validation/use-user-input-validation-result';
import { type UserInputValidationError } from '@tectonics/prs/validation/types';
import { serverErrorMessage } from '@tectonics/prs/validation/server-error-message';
import { useSelectedCardBrand } from '@tectonics/look-and-feel/commons/selected-card-brand';
import { useSubmitting } from '@tectonics/prs/submitting';

import { TextField } from '../../../../../atoms';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import cardGenericBackSvgSrc from './card-icon/images/card-generic-back.svg';
import { ErrorLabel } from '../../../../../molecules/error-label';
import { translate } from '@tectonics/translate';
import { PeruCVVTooltip } from '@tectonics/look-and-feel/volcanes/pages/page-opened-payment/payment-method/card-form/peruCvvTooltip';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import PERU from '@tectonics/commons/country/countries/peru';

type Props = {
  rootStyle?: {},
};

type SecurityCodeMask = ImmutableArray<RegExp | string>;

export function FieldCardSecurityCode({ rootStyle }: Props): Node {
  const inputId = useUniqueId();
  const brand = useSelectedCardBrand();
  const mask = getSecurityCodeMask(brand);
  const [value, handleChange] = useUserInputAsTextFieldWithFormatting('cardSecurityCode', formatRaw(mask), parseFormatted);
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('cardSecurityCode');
  const submitting = useSubmitting();
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  return (
    <div style={rootStyle} data-test-name="card-security-code">
      <label htmlFor={inputId}>
        <LabelMessage />
      </label>
      {country === PERU.code && <PeruCVVTooltip/>}

      <InputWrapper>
        <TextField
          as={InputMask}
          id={inputId}
          mask={mask}
          type="tel"
          value={value}
          onChange={handleChange}
          fashion={formHasBeenSent && validationError ? 'error' : null}
          readOnly={submitting}
          data-error={formHasBeenSent && validationError ? 'error' : null}
          data-secret
          name="card-security-code"
        />

        <CardImage src={cardGenericBackSvgSrc} alt="card cvv icon" />
      </InputWrapper>

      {formHasBeenSent && validationError && (
        <ValidationMessage validationError={validationError} />
      )}
    </div>
  );
}

type ValidationMessageProps = {|
  validationError: UserInputValidationError,
|};

const messages = (validationError: UserInputValidationError) => {
  switch(validationError.id){
    case 'empty':
      return translate('volcanes.emptyCardSecurityCode');

    case 'invalid-pattern':
      return translate('volcanes.invalidSecurityCode');

    default:
      return translate('volcanes.invalidCvv');
  }
};

function ValidationMessage({ validationError }: ValidationMessageProps): Node {
  return (
    <ErrorLabel>
      {messages(validationError) || (serverErrorMessage(validationError.payload))}
    </ErrorLabel>
  );
}

function LabelMessage(): Node {
  return translate('volcanes.fieldCardSecurityCode');
}

function formatRaw(mask: SecurityCodeMask): (rawValue: string) => string {
  return (rawValue: string): string => formatValueWithMask(rawValue, mask);
}

function parseFormatted(formattedValue: string): string {
  return formattedValue.replace(/[^0-9]+/ug, '');
}

function getSecurityCodeMask(cardBrand: ?CardBrand): SecurityCodeMask {
  switch (cardBrand) {
    case 'amex':
      return [/[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/];

    default:
      return [/[0-9]/, /[0-9]/, /[0-9]/];
  }
}

const InputWrapper = styled.div`
  position: relative;
`;

const CardImage = styled.img`
  position: absolute;
  top: 50%;
  margin-top: -10px;
  pointer-events: none;

  [dir=ltr] & {
    right: 16px;
  }

  [dir=rtl] & {
    left: 16px;
  }
`;
